import React, { useEffect, useState } from "react";
import { FiClock, FiCheckCircle, FiXCircle, FiAlertCircle, FiSlash, FiChevronDown, FiChevronUp, FiPhone, FiHome, FiMail } from "react-icons/fi";
import { BASEURL, ALL_REQUEST_STATUS } from "../constants";

const AgentChangeRequests = () => {
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [expandedRequest, setExpandedRequest] = useState(null);

  const getChangeRequests = async () => {
    setLoading(true);
    try {
      const res = await fetch(ALL_REQUEST_STATUS, {
        method: "GET",
        headers: {
          "ngrok-skip-browser-warning": "true",
        },
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch change requests (${res.status})`);
      }

      const data = await res.json();
      setRequests(Array.isArray(data) ? data : []);
    } catch (error) {
      console.log(error);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getChangeRequests();
  }, []);

  const toggleAccordion = (requestId) => {
    setExpandedRequest(expandedRequest === requestId ? null : requestId);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 0:
        return <FiClock className="w-5 h-5 text-yellow-500" />;
      case 1:
        return <FiXCircle className="w-5 h-5 text-red-500" />;
      case 2:
      return <FiCheckCircle className="w-5 h-5 text-green-500" />;
      case 3:
      return <FiSlash className="w-5 h-5 text-orange-500" />;
      default:
        return <FiAlertCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 0:
        return "bg-yellow-50 border-yellow-200 dark:bg-yellow-100";
      case 1:
        return "bg-red-50 border-red-200 dark:bg-red-100";
      case 2:
        return "bg-green-50 border-green-200 dark:bg-green-100";
      case 3:
        return "bg-orange-50 border-orange-200 dark:bg-orange-100";
      default:
        return "bg-gray-50 border-gray-200 dark:bg-white/5";
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 0:
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-200 dark:text-yellow-600";
      case 1:
        return "bg-red-100 text-red-800 dark:bg-red-200 dark:text-red-600";
      case 2:
        return "bg-green-100 text-green-800 dark:bg-green-200 dark:text-green-600";
      case 3:
        return "bg-orange-100 text-orange-800 dark:bg-orange-200 dark:text-orange-600";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-white/10 dark:text-gray-600";
    }
  };

  const renderRequestData = (data) => {
    if (!data) return null;

    return (
      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-neutral-700 space-y-4">

        {/* Contact Information */}
        <div className="bg-white dark:bg-neutral-900/90 rounded-lg border border-gray-200 dark:border-neutral-700 overflow-hidden shadow-sm">
          <div className="px-4 py-3 bg-gray-50 dark:bg-neutral-900/50 border-b border-gray-200 dark:border-neutral-700">
            <div className="flex items-center gap-2">
              <FiPhone className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <h4 className="font-medium text-gray-900 dark:text-gray-100">
                Contact Information
              </h4>
            </div>
          </div>

          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-md p-3 transition-colors">
                <p className="text-xs text-gray-500 dark:text-gray-50 mb-1">Email</p>
                <p className="text-sm text-gray-900 dark:text-gray-300 break-words">
                  {data.email ?? "N/A"}
                </p>
              </div>

              <div className="rounded-md p-3 transition-colors">
                <p className="text-xs text-gray-500 dark:text-gray-50 mb-1">Mobile</p>
                <p className="text-sm text-gray-900 dark:text-gray-300 break-words">
                  {data.mobile ?? "N/A"}
                </p>
              </div>

              <div className="rounded-md p-3 transition-colors">
                <p className="text-xs text-gray-500 dark:text-gray-50 mb-1">Phone</p>
                <p className="text-sm text-gray-900 dark:text-gray-300 break-words">
                  {data.phone ?? "N/A"}
                </p>
              </div>

              <div className="rounded-md p-3 transition-colors">
                <p className="text-xs text-gray-500 dark:text-gray-50 mb-1">Fax</p>
                <p className="text-sm text-gray-900 dark:text-gray-300 break-words">
                  {data.fax ?? "N/A"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Residential Address */}
        <div className="bg-white dark:bg-neutral-900/90 rounded-lg border border-gray-200 dark:border-neutral-700 overflow-hidden shadow-sm">
          <div className="px-4 py-3 bg-gray-50 dark:bg-neutral-900/50 border-b border-gray-200 dark:border-neutral-700">
            <div className="flex items-center gap-2">
              <FiHome className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <h4 className="font-medium text-gray-900 dark:text-gray-300">
                Residential Address
              </h4>
            </div>
          </div>

          <div className="p-4">
            <div className="space-y-2">
              <div className="ml-4">
                {data.localAddressLine1 && (
                  <p className="text-sm text-gray-900 dark:text-gray-300 break-words">
                    {data.localAddressLine1}
                  </p>
                )}
                {data.localAddressLine2 && (
                  <p className="text-sm text-gray-900 dark:text-gray-300 break-words">
                    {data.localAddressLine2}
                  </p>
                )}
                {data.localAddressLine3 && (
                  <p className="text-sm text-gray-900 dark:text-gray-300 break-words">
                    {data.localAddressLine3}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
                <div className="rounded-md p-3 transition-colors">
                  <p className="text-xs text-gray-500 dark:text-gray-50 mb-1">Suburb</p>
                  <p className="text-sm text-gray-900 dark:text-gray-300 break-words">
                    {data.lSuburb ?? "N/A"}
                  </p>
                </div>

                <div className="rounded-md p-3 transition-colors">
                  <p className="text-xs text-gray-500 dark:text-gray-50 mb-1">State</p>
                  <p className="text-sm text-gray-900 dark:text-gray-300 break-words">
                    {data.lState ?? "N/A"}
                  </p>
                </div>

                <div className="rounded-md p-3 transition-colors">
                  <p className="text-xs text-gray-500 dark:text-gray-50 mb-1">Postcode</p>
                  <p className="text-sm text-gray-900 dark:text-gray-300 break-words">
                    {data.lPostCode ?? "N/A"}
                  </p>
                </div>

                <div className="rounded-md p-3 transition-colors">
                  <p className="text-xs text-gray-500 dark:text-gray-50 mb-1">Country</p>
                  <p className="text-sm text-gray-900 dark:text-gray-300 break-words">
                    {data.lCountry ?? "N/A"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mailing Address */}
        <div className="bg-white dark:bg-neutral-900/90 rounded-lg border border-gray-200 dark:border-neutral-700 overflow-hidden shadow-sm">
          <div className="px-4 py-3 bg-gray-50 dark:bg-neutral-900/50 border-b border-gray-200 dark:border-neutral-700">
            <div className="flex items-center gap-2">
              <FiMail className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <h4 className="font-medium text-gray-900 dark:text-gray-100">
                Mailing Address
              </h4>
            </div>
          </div>

          <div className="p-4">
            <div className="space-y-2">
              <div className="ml-4">
                {data.mailingAddressLine1 && (
                  <p className="text-sm text-gray-900 dark:text-gray-100 break-words">
                    {data.mailingAddressLine1}
                  </p>
                )}
                {data.mailingAddressLine2 && (
                  <p className="text-sm text-gray-900 dark:text-gray-100 break-words">
                    {data.mailingAddressLine2}
                  </p>
                )}
                {data.mailingAddressLine3 && (
                  <p className="text-sm text-gray-900 dark:text-gray-100 break-words">
                    {data.mailingAddressLine3}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
                <div className="rounded-md p-3 transition-colors">
                  <p className="text-xs text-gray-500 dark:text-gray-50 mb-1">Suburb</p>
                  <p className="text-sm text-gray-900 dark:text-gray-100 break-words">
                    {data.mSuburb ?? "N/A"}
                  </p>
                </div>

                <div className="rounded-md p-3 transition-colors">
                  <p className="text-xs text-gray-500 dark:text-gray-50 mb-1">State</p>
                  <p className="text-sm text-gray-900 dark:text-gray-100 break-words">
                    {data.mState ?? "N/A"}
                  </p>
                </div>

                <div className="rounded-md p-3 transition-colors">
                  <p className="text-xs text-gray-500 dark:text-gray-50 mb-1">Postcode</p>
                  <p className="text-sm text-gray-900 dark:text-gray-100 break-words">
                    {data.mPostCode ?? "N/A"}
                  </p>
                </div>

                <div className="rounded-md p-3 transition-colors">
                  <p className="text-xs text-gray-500 dark:text-gray-50 mb-1">Country</p>
                  <p className="text-sm text-gray-900 dark:text-gray-100 break-words">
                    {data.mCountry ?? "N/A"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    );
  };

  
  const SkeletonCard = () => (
    <div className="rounded-lg border border-gray-200 dark:border-neutral-700 p-4 bg-white/60 dark:bg-white/5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-gray-200 dark:bg-white/10 animate-pulse" />
          <div className="h-5 w-20 rounded-full bg-gray-200 dark:bg-white/10 animate-pulse" />
        </div>
        <div className="h-4 w-16 rounded bg-gray-200 dark:bg-white/10 animate-pulse" />
      </div>

      <div className="space-y-2 mb-3">
        <div className="h-4 w-full rounded bg-gray-200 dark:bg-white/10 animate-pulse" />
        <div className="h-4 w-10/12 rounded bg-gray-200 dark:bg-white/10 animate-pulse" />
        <div className="h-4 w-8/12 rounded bg-gray-200 dark:bg-white/10 animate-pulse" />
      </div>

      <div className="flex items-center justify-between">
        <div className="h-3 w-24 rounded bg-gray-200 dark:bg-white/10 animate-pulse" />
        <div className="h-3 w-14 rounded bg-gray-200 dark:bg-white/10 animate-pulse" />
      </div>
    </div>
  );

  if (!loading && requests.length === 0) {
    return (
      <div>
        <div className="bg-white dark:bg-white/5 rounded-xl shadow-sm border border-gray-200 dark:border-neutral-700 p-12 text-center">
          <FiAlertCircle className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-300 text-lg">No change requests found</p>
        </div>
      </div>
    );
  }

  return (
  <div>
    <div className="bg-white dark:bg-white/5 rounded-xl border border-gray-200 dark:border-neutral-700 p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
        Change Requests
      </h2>

      <div className="overflow-hidden overflow-y-auto max-h-[450px] pr-1">
        {loading ? (
          [...Array(1)].map((_, i) => <SkeletonCard key={i} />)
        ) : (
          requests.map((request) => {
            const isExpanded = expandedRequest === request.requestId;

            return (
              <div
                key={request.requestId}
                className={`rounded-lg border p-4 my-3 transition-all hover:shadow-md ${getStatusColor(
                  request.status
                )}`}
              >
                <button type="button" className="w-full flex items-center justify-between mb-3 text-left" onClick={() => toggleAccordion(request.requestId)} aria-expanded={isExpanded}>
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-md bg-white dark:bg-gray/20 flex items-center justify-center shadow-sm">
                      {getStatusIcon(request.status)}
                    </div>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(request.status)}`}>
                      {request.statusName}
                    </span>
                  </div>

                  {isExpanded ? (
                    <FiChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <FiChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </button>

                {isExpanded && (
                  <div className="px-4 sm:px-5 pb-5">
                    {renderRequestData(request.requestData)}
                  </div>
                )}

                <p className="text-sm text-gray-700 dark:text-gray-500 leading-snug mb-3 line-clamp-3">
                  {request.statusMessage}
                </p>

                <div className="flex items-center justify-end text-xs text-gray-500 dark:text-gray-400">
                  <span>{request.requestDate}</span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  </div>
);


};

export default AgentChangeRequests;
